import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { CANVAS_PERSIST_DEBOUNCE_MS } from "../../../features/canvas/canvasDefaults";
import {
  createEmptyCanvasContent,
  createEmptyCanvasSnapshot,
  parseCanvasContent,
  serializeCanvasContent,
} from "../../../features/canvas/canvasContent";
import { getDefaultCanvasTool } from "../../../features/canvas/canvasEnvironment";
import { createCanvasEngine } from "../../../features/canvas/engine/canvasEngine";
import { CANVAS_TOOLS } from "../../../features/canvas/nativeCanvasElements";
import {
  readCanvasThemePalette,
  resolveShapeFillColor,
  resolveStrokeColor,
} from "../../../features/canvas/renderer/canvasTheme";
import { measureImageNaturalSize } from "./imageNaturalSize";
import { useCanvasKeyboard } from "./useCanvasKeyboard";
import { useCanvasPaste } from "./useCanvasPaste";

const getSnapshotFromContent = (content) =>
  parseCanvasContent(content)?.snapshot ?? createEmptyCanvasSnapshot();

const readImageFile = ({ file, onLoad }) => {
  const reader = new FileReader();
  reader.onload = () => onLoad(String(reader.result));
  reader.readAsDataURL(file);
};

/**
 * Owns the canvas engine for a workspace instance: creates it once, keeps it
 * in sync with the external `value` string, emits debounced serialized
 * content through `onChange`, and exposes the small reactive UI snapshot.
 * @param {{ onChange: (content: string) => void, onEscapeIdle?: Function, value: string }} params
 * @returns {object}
 */
export const useCanvasEngine = ({
  onChange,
  onEscapeIdle,
  onSaveShortcut,
  value,
}) => {
  const [engine] = useState(() =>
    createCanvasEngine({
      defaultTool: getDefaultCanvasTool(),
      snapshot: getSnapshotFromContent(value),
    })
  );
  const lastEmittedContentRef = useRef(value || createEmptyCanvasContent());
  const imageInputRef = useRef(null);
  const [themePalette, setThemePalette] = useState(readCanvasThemePalette);
  const ui = useSyncExternalStore(engine.subscribeUi, engine.getUiSnapshot);

  // Swatch colors resolve through CSS theme variables; re-read the palette
  // when the app theme flips (the observer fires after data-theme lands).
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() =>
      setThemePalette(readCanvasThemePalette())
    );
    observer.observe(document.documentElement, {
      attributeFilter: ["data-theme"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (value === lastEmittedContentRef.current) return;
    lastEmittedContentRef.current = value || createEmptyCanvasContent();
    engine.loadSnapshot(getSnapshotFromContent(value));
  }, [engine, value]);

  useEffect(() => {
    let timeoutId = null;
    const emitContent = () => {
      const nextContent = serializeCanvasContent(engine.serializeSnapshot());
      if (nextContent === lastEmittedContentRef.current) return;
      lastEmittedContentRef.current = nextContent;
      onChange(nextContent);
    };
    const unsubscribe = engine.subscribeScene(() => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        emitContent();
      }, CANVAS_PERSIST_DEBOUNCE_MS);
    });

    return () => {
      unsubscribe();
      if (!timeoutId) return;
      clearTimeout(timeoutId);
      emitContent();
    };
  }, [engine, onChange]);

  const getContent = useCallback(
    () => serializeCanvasContent(engine.serializeSnapshot()),
    [engine]
  );

  const openImagePicker = useCallback(() => imageInputRef.current?.click(), []);

  const addImageFromDataUrl = useCallback(
    async (dataUrl) => {
      const naturalSize = await measureImageNaturalSize(dataUrl);
      engine.addImage(dataUrl, naturalSize);
    },
    [engine]
  );

  const handleToolChange = useCallback(
    (tool) => {
      if (tool === CANVAS_TOOLS.IMAGE) return openImagePicker();
      engine.setTool(tool);
    },
    [engine, openImagePicker]
  );

  const handleImageSelect = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      readImageFile({ file, onLoad: addImageFromDataUrl });
    },
    [addImageFromDataUrl]
  );

  useCanvasKeyboard({
    engine,
    onEscapeIdle,
    onImageToolRequest: openImagePicker,
    onSaveShortcut,
  });
  useCanvasPaste({
    onElementsPaste: engine.pasteElements,
    onImagePaste: addImageFromDataUrl,
  });

  // Persisted default colors are theme-authored sentinels (e.g. the dark
  // theme's default shape fill); resolve them against the active theme so
  // the style-bar swatches never show a stale color from another theme.
  const displayedSelectedStyle = useMemo(
    () => ({
      ...ui.selectedStyle,
      fill: resolveShapeFillColor({
        color: ui.selectedStyle.fill,
        palette: themePalette,
      }),
      stroke: resolveStrokeColor({
        color: ui.selectedStyle.stroke,
        palette: themePalette,
      }),
    }),
    [themePalette, ui]
  );

  return {
    canRedo: ui.canRedo,
    canUndo: ui.canUndo,
    editingElementId: ui.editingElementId,
    editingValue: ui.editingValue,
    engine,
    getContent,
    handleImageSelect,
    handleToolChange,
    imageInputRef,
    isToolLocked: ui.isToolLocked,
    selectedCount: ui.selectedCount,
    selectedStyle: displayedSelectedStyle,
    selectedTool: ui.tool,
    zoomLabel: `${ui.zoomPercent}%`,
  };
};
